
/**
 * Procurement acronyms and abbreviations used in TED notices
 */

export interface ProcurementAcronym {
  acronym: string;
  fullName: string;
  description: string;
}

export const PROCUREMENT_ACRONYMS: ProcurementAcronym[] = [
  {
    acronym: "BG",
    fullName: "Business Group",
    description: "A group of related businesses or business units within the procurement context."
  },
  {
    acronym: "BRIN",
    fullName: "Business Register Information Notice",
    description: "Notice containing information about business registration, European companies, or economic interest groupings."
  },
  {
    acronym: "BT",
    fullName: "Business Term",
    description: "A defined term used in eForms and procurement documentation."
  },
  {
    acronym: "CA",
    fullName: "Contracting Authority",
    description: "A public sector entity that awards contracts for public works, supplies or services."
  },
  {
    acronym: "CAN",
    fullName: "Contract Award Notice",
    description: "A notice published after a contract has been awarded, announcing the successful tenderer."
  },
  {
    acronym: "CE",
    fullName: "Contracting Entity",
    description: "An entity operating in specific sectors (water, energy, transport and postal services) that awards contracts."
  },
  {
    acronym: "CFC",
    fullName: "Call For Competition",
    description: "A notice inviting economic operators to participate in a procurement procedure."
  },
  {
    acronym: "CN",
    fullName: "Contract Notice",
    description: "A notice announcing a new procurement opportunity."
  },
  {
    acronym: "CPB",
    fullName: "Central Purchasing Body",
    description: "An entity that centralizes purchasing activities for multiple contracting authorities."
  },
  {
    acronym: "DAP",
    fullName: "Direct Award Prenotification",
    description: "Advance notification of intent to award a contract directly without competition."
  },
  {
    acronym: "DPS",
    fullName: "Dynamic Purchasing System",
    description: "An electronic process for commonly used purchases that are generally available on the market."
  },
  {
    acronym: "ePO",
    fullName: "eProcurement Ontology",
    description: "A standardized conceptual model for public procurement data."
  },
  {
    acronym: "ESPD",
    fullName: "European Single Procurement Document",
    description: "A self-declaration form used as preliminary evidence of eligibility to participate in procurement procedures."
  },
  {
    acronym: "FA",
    fullName: "Framework Agreement",
    description: "An agreement between one or more contracting authorities and one or more economic operators to establish terms for contracts to be awarded during a given period."
  },
  {
    acronym: "OJEU",
    fullName: "Official Journal of the European Union",
    description: "The official publication for EU legislation, information and notices including procurement notices."
  },
  {
    acronym: "OP",
    fullName: "Publications Office of the European Union",
    description: "The EU office responsible for publishing EU official documents including procurement notices."
  },
  {
    acronym: "OPP",
    fullName: "OP Production Term",
    description: "A term used in the production of procurement notices by the Publications Office."
  },
  {
    acronym: "OPT",
    fullName: "OP Technical Term",
    description: "A technical term used in procurement notices by the Publications Office."
  },
  {
    acronym: "PIN",
    fullName: "Prior Information Notice or Periodic Indicative Notice",
    description: "A notice announcing future procurement plans to the market in advance of formal tender procedures."
  },
  {
    acronym: "QS",
    fullName: "Qualification System",
    description: "A system used by utility contracting entities to pre-qualify suppliers for future contracts."
  },
  {
    acronym: "RTL / TL",
    fullName: "Reduce Time Limit / Time Limit",
    description: "Provisions allowing for shorter time limits in procurement procedures under specific circumstances."
  },
  {
    acronym: "TED",
    fullName: "Tenders Electronic Daily",
    description: "The online version of the 'Supplement to the Official Journal of the EU', dedicated to European public procurement."
  },
  {
    acronym: "VEAT",
    fullName: "Voluntary Ex-Ante Transparency Notice",
    description: "A notice published voluntarily before contract award to ensure transparency for certain contracts awarded without prior publication."
  },
  
  // Notice Type Codes
  {
    acronym: "can-modif",
    fullName: "Contract Modification Notice",
    description: "A notice informing about modifications to an existing contract."
  },
  {
    acronym: "can-social", 
    fullName: "Contract or Concession Award Notice - Light Regime",
    description: "Award notice for contracts under the simplified 'light regime' for certain services."
  },
  {
    acronym: "can-standard",
    fullName: "Contract or Concession Award Notice - Standard Regime",
    description: "Standard award notice for contracts or concessions."
  },
  {
    acronym: "can-tran",
    fullName: "Contract Award Notice for Public Passenger Transport Services",
    description: "Specific award notice for public passenger transport services contracts."
  },
  {
    acronym: "can-desg",
    fullName: "Design Contest Result Notice",
    description: "Notice announcing the result of a design contest."
  },
  {
    acronym: "cn-social",
    fullName: "Contract Notice - Light Regime",
    description: "Contract notice under the simplified 'light regime' for certain services."
  },
  {
    acronym: "cn-standard",
    fullName: "Contract or Concession Notice - Standard Regime",
    description: "Standard contract notice for new procurement opportunities."
  },
  {
    acronym: "cn-desg",
    fullName: "Design Contest Notice",
    description: "A notice inviting participation in a design contest."
  },
  {
    acronym: "compl",
    fullName: "Contract Completion Notice",
    description: "Notice indicating the completion of a contract."
  },
  {
    acronym: "corr",
    fullName: "Change Notice",
    description: "Notice correcting or updating information in a previously published notice."
  },
  {
    acronym: "pin-buyer",
    fullName: "Notice of Prior Information Notice Publication",
    description: "Notice informing about the publication of a prior information notice on a buyer profile."
  },
  {
    acronym: "pin-cfc-social",
    fullName: "Prior Information Notice as Call for Competition - Light Regime",
    description: "PIN used as a call for competition under the light regime."
  },
  {
    acronym: "pin-cfc-standard",
    fullName: "Prior Information Notice as Call for Competition - Standard Regime",
    description: "PIN used as a call for competition under the standard regime."
  },
  {
    acronym: "pin-only",
    fullName: "Prior Information Notice - Information Only",
    description: "PIN used only for information purposes, not as a call for competition."
  },
  {
    acronym: "pin-rtl",
    fullName: "Prior Information Notice - Shortened Time Limits",
    description: "PIN used to justify shortened time limits for receipt of tenders."
  },
  {
    acronym: "pin-tran",
    fullName: "Prior Information Notice for Public Passenger Transport",
    description: "Specific prior information notice for public passenger transport services."
  },
  {
    acronym: "pmc",
    fullName: "Pre-Market Consultation Notice",
    description: "Notice announcing a consultation with the market before formal procurement."
  },
  {
    acronym: "qu-sy",
    fullName: "Qualification System Notice",
    description: "Notice on the existence of a qualification system for suppliers."
  },
  {
    acronym: "subco",
    fullName: "Subcontracting Notice",
    description: "Notice related to subcontracting opportunities within a contract."
  },
  {
    acronym: "brin-eeig",
    fullName: "European Economic Interest Grouping Notice",
    description: "Notice relating to the formation or liquidation of a European Economic Interest Grouping."
  },
  {
    acronym: "brin-ecs",
    fullName: "European Company/European Cooperative Society Notice",
    description: "Notice relating to European Companies or European Cooperative Societies."
  }
];

/**
 * Finds an acronym by its abbreviation
 */
export function findAcronym(acronym: string): ProcurementAcronym | undefined {
  return PROCUREMENT_ACRONYMS.find(item => 
    item.acronym.toLowerCase() === acronym.toLowerCase()
  );
}

/**
 * Checks if a given string is a known procurement acronym
 */
export function isKnownAcronym(text: string): boolean {
  return PROCUREMENT_ACRONYMS.some(item => 
    item.acronym.toLowerCase() === text.toLowerCase()
  );
}

/**
 * Detects and returns all acronyms in a given text
 */
export function detectAcronyms(text: string): ProcurementAcronym[] {
  if (!text) return [];
  
  const words = text.split(/\s+/);
  const foundAcronyms: ProcurementAcronym[] = [];
  
  words.forEach(word => {
    // Clean word from punctuation
    const cleanWord = word.replace(/[.,;:!?()\[\]{}'"]/g, '');
    
    const acronym = PROCUREMENT_ACRONYMS.find(item => 
      item.acronym.toLowerCase() === cleanWord.toLowerCase()
    );
    
    if (acronym && !foundAcronyms.some(a => a.acronym === acronym.acronym)) {
      foundAcronyms.push(acronym);
    }
  });
  
  return foundAcronyms;
}
